import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '../services/notificationService';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export const useNotifications = (params = {}) => {
  return useQuery(
    ['notifications', params],
    () => notificationService.getNotifications(params),
    {
      keepPreviousData: true,
      staleTime: 30000,
      refetchInterval: 60000
    }
  );
};

export const useUnreadNotifications = () => {
  return useQuery(
    ['unread-notifications'],
    notificationService.getUnreadNotifications,
    {
      staleTime: 30000,
      refetchInterval: 30000
    }
  );
};

export const useNotificationStats = () => {
  return useQuery(
    ['notification-stats'],
    notificationService.getNotificationStats,
    {
      staleTime: 60000
    }
  );
};

export const useNotification = (notificationId) => {
  return useQuery(
    ['notification', notificationId],
    () => notificationService.getNotification(notificationId),
    {
      enabled: !!notificationId,
      staleTime: 60000
    }
  );
};

export const useCreateNotification = () => {
  const queryClient = useQueryClient();

  return useMutation(notificationService.createNotification, {
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notification-stats']);
      toast.success('Notification created successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create notification');
    }
  });
};

export const useCreateBulkNotifications = () => {
  const queryClient = useQueryClient();

  return useMutation(notificationService.createBulkNotifications, {
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notification-stats']);
      toast.success('Bulk notifications created successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create bulk notifications');
    }
  });
};

export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation(notificationService.markAsRead, {
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['unread-notifications']);
      queryClient.invalidateQueries(['notification-stats']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to mark notification as read');
    }
  });
};

export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation(notificationService.markAllAsRead, {
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['unread-notifications']);
      queryClient.invalidateQueries(['notification-stats']);
      toast.success('All notifications marked as read');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to mark all notifications as read');
    }
  });
};

export const useDeleteNotification = () => {
  const queryClient = useQueryClient();

  return useMutation(notificationService.deleteNotification, {
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notification-stats']);
      toast.success('Notification deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete notification');
    }
  });
};

// Legacy hook for backward compatibility
export const useNotificationsLegacy = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [unreadCount, setUnreadCount] = useState(0);

  const {
    data: notificationsData,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['notifications', user?.id],
    () => notificationService.getNotifications({ limit: 50 }),
    {
      enabled: !!user,
      refetchInterval: 30000,
      onSuccess: data => {
        const unread = data.notifications?.filter(n => !n.is_read).length || 0;
        setUnreadCount(unread);
      }
    }
  );

  const markAsReadMutation = useMutation(notificationService.markAsRead, {
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
    }
  });

  const markAllAsReadMutation = useMutation(notificationService.markAllAsRead, {
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      setUnreadCount(0);
    }
  });

  const deleteNotificationMutation = useMutation(
    notificationService.deleteNotification,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
      }
    }
  );

  const notifications = notificationsData?.notifications || [];

  const markAsRead = notificationId => {
    markAsReadMutation.mutate(notificationId);
  };

  const markAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const deleteNotification = notificationId => {
    deleteNotificationMutation.mutate(notificationId);
  };

  // WebSocket for real-time notifications (optional)
  useEffect(() => {
    if (!user || !import.meta.env.VITE_WS_URL) return;

    const ws = new WebSocket(
      `${import.meta.env.VITE_WS_URL}/notifications/${user.id}`
    );

    ws.onmessage = event => {
      const notification = JSON.parse(event.data);

      // Update cache with new notification
      queryClient.setQueryData(['notifications', user.id], oldData => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          notifications: [notification, ...oldData.notifications]
        };
      });

      // Update unread count
      setUnreadCount(prev => prev + 1);

      // Show toast notification if enabled
      if (import.meta.env.VITE_ENABLE_NOTIFICATIONS === 'true') {
        // You can add toast notification here
      }
    };

    ws.onerror = error => {
      console.error('WebSocket error:', error);
    };

    return () => {
      ws.close();
    };
  }, [user, queryClient]);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refetch,
    isMarkingAsRead: markAsReadMutation.isLoading,
    isMarkingAllAsRead: markAllAsReadMutation.isLoading,
    isDeleting: deleteNotificationMutation.isLoading
  };
};
